/**
 * El editor visual de distribución.
 *
 * Fusiona lo que antes eran dos pantallas separadas — el selector de modo de
 * `AllocationModal` y la rejilla de `PeriodGrid` — en un solo lugar: arriba,
 * la regla vigente (quién paga por defecto, versionada); abajo, la línea de
 * tiempo del año, agrupada en **bloques** que se arrastran para mover,
 * extender o asignar una excepción sin abrir un formulario por mes.
 *
 * La regla y las excepciones siguen siendo dos conceptos distintos en el
 * dominio (`subscription_allocations` vs. `allocation_period_overrides`) y
 * acá también: cambiar la regla abre una versión nueva (nunca reescribe
 * meses ya repartidos); arrastrar un bloque solo toca la excepción de esos
 * meses puntuales. El servidor decide siempre la excepción sobre la regla —
 * acá solo se pinta lo que `/periods` ya resolvió así.
 *
 * Reusable desde el detalle de un consultorio o el de un paquete: solo pide
 * `subscriptionId`, y se puede deep-linkear con eso nada más.
 */

import { useMemo, useRef, useState } from 'react';
import {
  ALLOCATION_MODE, ALLOCATION_MODE_HINT, ALLOCATION_MODE_LABEL, dateLabel, periodLabel, periodLabelShort,
  startOfMonth, today, type AllocationMode,
} from '@cleanblue/shared';
import { api } from '@/lib/api';
import { useApi, useApiMutation } from '@/lib/hooks';
import { haptic } from '@/lib/haptics'; // HAPTIC_FEEDBACK
import { Icon } from '@/components/common/NavIcons';
import { payerColor } from './PeriodGrid';
import {
  Async, Badge, Button, Card, Empty, Field, Modal, Money, Note, Stat,
} from '@/components/ui';

/* ── Tipos que llegan del Worker ──────────────────────────────────────────── */

interface Candidate {
  party_id: string; name: string; kind: 'MEMBER' | 'UNIT';
  legal_name: string | null; tax_id: string | null; billing_email: string | null;
}

interface Allocation {
  id: string; mode: AllocationMode; effective_from: string; effective_to: string | null;
  rotation_anchor: string | null; confirmed: number; notes: string | null;
}

interface Member {
  id: string; allocation_id: string; party_id: string; ordinal: number;
  percentage: number | null; fixed_amount_minor: number | null; rotation_seq: number | null;
  display_name: string; kind: 'MEMBER' | 'UNIT'; legal_name: string | null; tax_id: string | null;
}

interface SubscriptionInfo {
  id: string; service_unit_id: string; unit_code: string; unit_name: string | null;
  service_name: string; base_price_minor: number; price_override_minor: number | null;
}

interface DistributionDetail {
  subscription: SubscriptionInfo;
  allocations: Allocation[];
  members: Member[];
}

interface BaselineMonth {
  period_start: string; skip_reason: string | null; total: string | null; rotation_index: number | null;
  // `name` es el receptor fiscal (para facturar); `party_name` es quién paga
  // de verdad. Distribución de pagos usa `party_name` — mezclar los dos fue
  // lo que hacía ver a Cualitek como si rotara con Tito en el 511.
  payers: { party_id: string; name: string; party_name: string; due: string; share_basis: number | null }[];
  excluded_payers: { party_id: string; name: string; reason: string }[];
}

interface OverrideMonth {
  period_start: string; action: 'SKIP' | 'PRICE' | null; price_override_minor: number | null; note: string | null;
  payers: { party_id: string; display_name: string; percentage: number | null; fixed_amount_minor: number | null }[];
  locked: boolean; lock_reason: string | null;
}

interface GridData { year: number; months: string[]; baseline: BaselineMonth[]; overrides: OverrideMonth[] }

/* ── Resolver un mes: la excepción gana sobre la regla, siempre ──────────── */

interface DistPayer { party_id: string; name: string; percentage: number | null; fixed_amount_minor: number | null }

interface ResolvedMonth {
  period: string; payers: DistPayer[]; source: 'rule' | 'override' | 'skip' | 'empty';
  locked: boolean; lockReason: string | null; total: string | null;
}

function resolveMonth(base: BaselineMonth, ov: OverrideMonth | undefined): ResolvedMonth {
  const locked = ov?.locked ?? false;
  const lockReason = ov?.lock_reason ?? null;
  // «No cobrar este mes» — una excepción distinta de un mes que simplemente no
  // tiene a nadie asignado todavía: la primera es una decisión explícita
  // (`allocation_period_overrides`/`subscription_period_overrides`, acción
  // SKIP); la segunda ('empty') sigue pidiendo que alguien la resuelva.
  const skipped = ov?.action === 'SKIP' || Boolean(base.skip_reason);
  if (skipped) return { period: base.period_start, payers: [], source: 'skip', locked, lockReason, total: null };

  if ((ov?.payers.length ?? 0) > 0) {
    return {
      period: base.period_start,
      payers: ov!.payers.map((p) => ({
        party_id: p.party_id, name: p.display_name, percentage: p.percentage, fixed_amount_minor: p.fixed_amount_minor,
      })),
      source: 'override', locked, lockReason, total: base.total,
    };
  }

  if (base.payers.length > 0) {
    // OJO: `/periods` no manda `share_kind`, así que `share_basis` puede ser
    // un porcentaje o —si la regla vigente es FIXED_AMOUNTS— un monto en
    // céntimos, y no hay forma confiable de distinguirlos acá. Por eso un
    // pagador de la regla **no** carga percentage/fixed_amount_minor: mover o
    // extender un bloque de la regla reparte por igual entre sus personas en
    // vez de arriesgarse a leer un monto como si fuera un porcentaje.
    return {
      period: base.period_start,
      payers: base.payers.map((p) => ({ party_id: p.party_id, name: p.party_name, percentage: null, fixed_amount_minor: null })),
      source: 'rule', locked, lockReason, total: base.total,
    };
  }

  return { period: base.period_start, payers: [], source: 'empty', locked, lockReason, total: base.total };
}

function resolveMonths(grid: GridData): ResolvedMonth[] {
  const baseByPeriod = new Map(grid.baseline.map((b) => [b.period_start, b]));
  const ovByPeriod = new Map(grid.overrides.map((o) => [o.period_start, o]));
  return grid.months
    .map((period) => baseByPeriod.get(period))
    .filter((b): b is BaselineMonth => Boolean(b))
    .map((b) => resolveMonth(b, ovByPeriod.get(b.period_start)));
}

/**
 * Agrupa meses consecutivos con el mismo conjunto de pagadores y el mismo
 * origen (regla u override) en un solo bloque visual. Un mes vacío, sin
 * resolver, o bloqueado nunca se agrupa — siempre es su propio bloque de un
 * mes, igual que ya hacía cada celda de `PeriodGrid`.
 */
interface DistBlock {
  key: string; months: string[]; source: 'rule' | 'override' | 'skip' | 'empty';
  locked: boolean; lockReason: string | null; payers: DistPayer[]; monthlyTotal: string | null;
}

function buildBlocks(grid: GridData): DistBlock[] {
  /* CADA MES ES SU PROPIA CELDA — ya no se fusionan meses consecutivos del
     mismo pagador en un bloque grande.

     La fusión parecía elegante y produjo dos males a la vez (reporte del
     dueño, 28-ago-2026). Primero: cualquier edición —hasta soltar un chip—
     escribía sobre TODOS los meses del bloque, y si uno solo ya estaba
     cobrado el servidor rechazaba la operación entera: «no me está dejando
     editar los pagos en la rotación». Segundo: los meses «se fusionaron muy
     raro» — un año entero bajo la misma regla se veía como un bloque enorme,
     que es exactamente lo que él no quería.

     Cuando una FACTURA cubre varios meses (el caso histórico del 610: una de
     marzo atribuible a varios meses pendientes), eso se anota donde vive el
     hecho —en la factura, «compartida entre meses», pantalla de Facturación—
     no dibujando un bloque acá: el reparto de quién paga sigue siendo mes a
     mes. */
  return resolveMonths(grid).map((r) => ({
    key: r.period, months: [r.period], source: r.source, locked: r.locked,
    lockReason: r.lockReason, payers: r.payers, monthlyTotal: r.total,
  }));
}

function monthRangeLabel(months: string[]): string {
  if (months.length === 1) return periodLabelShort(months[0]!);
  return `${periodLabelShort(months[0]!)} – ${periodLabelShort(months[months.length - 1]!)}`;
}

/* ── Escribir excepciones: un solo punto para asignar, mover y extender ──── */

type MemberBody = { party_id: string; percentage?: number; fixed_amount?: number };

/**
 * Reparto en partes iguales que SUMA 100 EXACTO.
 *
 * `100 / 3` da 33.33 tres veces = 99.99, y el editor —con razón— no deja
 * guardar lo que no suma 100. Pero ese 99.99 lo generaba el propio sistema
 * como valor por defecto: la pantalla se bloqueaba sola y el dueño lo vivió
 * como «no me está dejando editar» (28-ago-2026). El residuo del redondeo
 * (0.01) se le da al primero: 33.34 / 33.33 / 33.33.
 */
function partesIguales(n: number): number[] {
  const base = Math.floor((100 / n) * 100) / 100;
  const resto = Math.round((100 - base * n) * 100) / 100;
  return Array.from({ length: n }, (_, i) => (i === 0 ? Math.round((base + resto) * 100) / 100 : base));
}

/** Si no se conoce con certeza el porcentaje o el monto de cada quien, se reparte por igual. */
function payersToBody(payers: DistPayer[]): MemberBody[] {
  if (payers.every((p) => p.percentage != null)) {
    return payers.map((p) => ({ party_id: p.party_id, percentage: p.percentage! }));
  }
  if (payers.every((p) => p.fixed_amount_minor != null)) {
    return payers.map((p) => ({ party_id: p.party_id, fixed_amount: p.fixed_amount_minor! }));
  }
  const pct = partesIguales(payers.length);
  return payers.map((p, i) => ({ party_id: p.party_id, percentage: pct[i]! }));
}

type WriteAction =
  | { type: 'set'; months: string[]; members: MemberBody[] }
  | { type: 'clear'; months: string[] }
  | { type: 'skip'; months: string[] }
  | { type: 'move'; originMonths: string[]; destMonths: string[]; members: MemberBody[] };

/** Todo lo que el editor visual escribe pasa por acá: un PUT o un DELETE por mes. */
function useWritePeriods(subscriptionId: string) {
  return useApiMutation(
    async (action: WriteAction) => {
      const toKey = (period: string) => period.slice(0, 7);
      /** Quita «no cobrar» de un mes — necesario antes de asignarle pagador,
          si no la excepción de no-cobro sigue ganando y el cobro nunca sale. */
      const clearSkip = (period: string) =>
        api.del(`/admin/subscriptions/${subscriptionId}/periods/${toKey(period)}`).catch(() => undefined);

      if (action.type === 'set') {
        await Promise.all(action.months.map(async (period) => {
          await clearSkip(period);
          await api.put(`/admin/subscriptions/${subscriptionId}/periods/${toKey(period)}/payers`, { members: action.members });
        }));
      } else if (action.type === 'clear') {
        await Promise.all(action.months.map((period) => Promise.all([
          api.del(`/admin/subscriptions/${subscriptionId}/periods/${toKey(period)}/payers`).catch(() => undefined),
          clearSkip(period),
        ])));
      } else if (action.type === 'skip') {
        await Promise.all(action.months.map((period) => Promise.all([
          api.del(`/admin/subscriptions/${subscriptionId}/periods/${toKey(period)}/payers`).catch(() => undefined),
          api.post('/admin/period-overrides', { subscription_id: subscriptionId, period_start: period, action: 'SKIP' }),
        ])));
      } else {
        await Promise.all(action.originMonths.map((period) =>
          api.del(`/admin/subscriptions/${subscriptionId}/periods/${toKey(period)}/payers`).catch(() => undefined)));
        await Promise.all(action.destMonths.map(async (period) => {
          await clearSkip(period);
          await api.put(`/admin/subscriptions/${subscriptionId}/periods/${toKey(period)}/payers`, { members: action.members });
        }));
      }
    },
    ['/admin/subscriptions', '/admin/preview', '/admin/matrix'],
  );
}

/* ── La línea de tiempo: bloques que se arrastran ─────────────────────────── */

type Pending =
  | { kind: 'simple'; label: string; action: WriteAction }
  | { kind: 'choice'; label: string; equal: WriteAction; replace: WriteAction; replaceLabel: string };

/** Encuentra qué mes hay bajo una coordenada X leyendo la fila de encabezados. */
function periodAtX(headEl: HTMLElement | null, clientX: number): string | null {
  if (!headEl) return null;
  const cells = headEl.querySelectorAll<HTMLElement>('[data-period]');
  for (const cell of Array.from(cells)) {
    const r = cell.getBoundingClientRect();
    if (clientX >= r.left && clientX < r.right) return cell.dataset.period ?? null;
  }
  return null;
}

/** El único pagador de un mes, si tiene exactamente uno — lo único que puede
 *  formar parte de una rotación detectada: dos personas o ninguna cortan la
 *  racha. */
function soloPagador(r: ResolvedMonth): string | null {
  return r.payers.length === 1 ? r.payers[0]!.party_id : null;
}

/**
 * Alrededor de `semilla` (el mes que se acaba de tocar), busca la racha más
 * larga de meses consecutivos de una sola persona que **alternan y se
 * repiten sin excepción** — nunca propone una rotación que no explique cada
 * mes de la racha tal cual está, ni una ya idéntica a la regla vigente: mejor
 * callar que ofrecer algo a medias (pedido del dueño, 3-sep-2026: definir la
 * rotación arrastrando en el propio calendario, sin abrir un panel aparte).
 */
function detectarRotacion(
  resueltos: ResolvedMonth[], semilla: string, vigente: Allocation | undefined,
): { anchor: string; partyIds: string[]; months: string[] } | null {
  const idx = resueltos.findIndex((r) => r.period === semilla);
  if (idx < 0) return null;

  let ini = idx;
  while (ini > 0 && soloPagador(resueltos[ini - 1]!) != null) ini--;
  let fin = idx;
  while (fin < resueltos.length - 1 && soloPagador(resueltos[fin + 1]!) != null) fin++;

  const racha = resueltos.slice(ini, fin + 1);
  if (racha.length < 2) return null;

  const partyIds: string[] = [];
  for (const r of racha) {
    const p = soloPagador(r)!;
    if (!partyIds.includes(p)) partyIds.push(p);
  }
  // La misma persona en toda la racha no es una rotación — es una sola persona.
  if (partyIds.length < 2) return null;

  const n = partyIds.length;
  const consistente = racha.every((r, i) => soloPagador(r) === partyIds[i % n]);
  if (!consistente) return null;

  const anchor = racha[0]!.period;
  // Ya es exactamente la regla vigente: nada nuevo que ofrecer.
  if (vigente?.mode === 'ROTATION' && vigente.rotation_anchor === anchor) return null;

  return { anchor, partyIds, months: racha.map((r) => r.period) };
}

function Timeline({ subscriptionId, year, setYear, grid, candidates, current }: {
  subscriptionId: string; year: number; setYear: (y: number) => void;
  grid: GridData; candidates: Candidate[]; current: Allocation | undefined;
}) {
  const headRef = useRef<HTMLDivElement>(null);
  const write = useWritePeriods(subscriptionId);
  const [pending, setPending] = useState<Pending | null>(null);
  const [editingBlock, setEditingBlock] = useState<DistBlock | null>(null);
  const [chipDrag, setChipDrag] = useState<
    { partyId: string; name: string; x: number; y: number; overKey: string | null } | null
  >(null);
  const [blockDrag, setBlockDrag] = useState<
    { kind: 'move' | 'extend'; block: DistBlock; overPeriod: string | null } | null
  >(null);

  const currentMonth = startOfMonth(today());
  const blocks = useMemo(() => buildBlocks(grid), [grid]);
  const blockByPeriod = useMemo(() => {
    const m = new Map<string, DistBlock>();
    for (const b of blocks) for (const p of b.months) m.set(p, b);
    return m;
  }, [blocks]);

  /* El mes que la última escritura tocó — la semilla desde la que
     `detectarRotacion` mira hacia atrás y hacia adelante. Vive acá, no
     adentro de `detectarRotacion`, porque el cálculo necesita saber DESDE
     DÓNDE mirar y eso es lo único que no se puede derivar de `grid` solo. */
  const [semilla, setSemilla] = useState<string | null>(null);
  const nombreDe = (partyId: string): string =>
    candidates.find((c) => c.party_id === partyId)?.name ?? 'alguien';
  const promocion = useMemo(() => {
    if (!semilla) return null;
    return detectarRotacion(resolveMonths(grid), semilla, current);
  }, [semilla, grid, current]);

  const promover = useApiMutation(
    (p: { anchor: string; partyIds: string[] }) => api.post('/admin/allocations', {
      subscription_id: subscriptionId,
      effective_from: p.anchor,
      mode: 'ROTATION',
      rotation_anchor: p.anchor,
      members: p.partyIds.map((id, i) => ({ party_id: id, ordinal: i + 1, rotation_seq: i })),
    }),
    ['/admin/subscriptions', '/admin/preview', '/admin/dashboard', '/admin/matrix'],
    { onSuccess: () => setSemilla(null) },
  );

  /** Envuelve `write.mutate`: además de escribir, guarda desde dónde mirar
   *  para la próxima detección. Todo el arrastre pasa por acá — nunca por
   *  `write.mutate` directo — para no tener que acordarse de repetirlo en
   *  cada punto donde se confirma un cambio. */
  function escribir(action: WriteAction, opts?: { onSuccess?: () => void }) {
    const months = action.type === 'move' ? action.destMonths : action.months;
    write.mutate(action, {
      onSuccess: () => {
        setSemilla(months[months.length - 1] ?? null);
        opts?.onSuccess?.();
      },
    });
  }

  /* Arrastrar una ficha de la paleta sobre un bloque. */
  function onChipDown(c: Candidate, e: React.PointerEvent<HTMLButtonElement>) {
    e.currentTarget.setPointerCapture(e.pointerId);
    setChipDrag({ partyId: c.party_id, name: c.name, x: e.clientX, y: e.clientY, overKey: null });
  }
  function onChipMove(e: React.PointerEvent<HTMLButtonElement>) {
    if (!chipDrag) return;
    const el = document.elementFromPoint(e.clientX, e.clientY);
    const cell = el?.closest<HTMLElement>('[data-block-start]');
    const locked = cell?.dataset.locked === '1';
    setChipDrag((d) => (d ? { ...d, x: e.clientX, y: e.clientY, overKey: cell && !locked ? cell.dataset.blockStart ?? null : null } : d));
  }
  function onChipUp() {
    if (chipDrag?.overKey) {
      const block = blocks.find((b) => b.key === chipDrag.overKey);
      if (block) dropChipOnBlock(block, chipDrag.partyId, chipDrag.name);
    }
    setChipDrag(null);
  }

  function dropChipOnBlock(block: DistBlock, partyId: string, partyName: string) {
    const rangeLabel = monthRangeLabel(block.months);
    const incomingIsUnit = candidates.find((candidate) => candidate.party_id === partyId)?.kind === 'UNIT';
    const blockHasUnit = block.payers.some((payer) =>
      candidates.find((candidate) => candidate.party_id === payer.party_id)?.kind === 'UNIT');
    if (block.payers.length > 0 && incomingIsUnit !== blockHasUnit) {
      setPending({
        kind: 'simple',
        label: `Cambiar la modalidad de ${rangeLabel}: ${partyName} será el único responsable.`,
        action: { type: 'set', months: block.months, members: [{ party_id: partyId, percentage: 100 }] },
      });
      return;
    }
    if (block.payers.length === 0) {
      setPending({
        kind: 'simple',
        label: `${partyName} paga ${rangeLabel} al 100%.`,
        action: { type: 'set', months: block.months, members: [{ party_id: partyId, percentage: 100 }] },
      });
      return;
    }
    if (block.payers.some((p) => p.party_id === partyId)) {
      // Ya está en el bloque. Con una sola persona no hay nada que cambiar,
      // pero con varias, volver a soltarla es la manera obvia de pedir «dejá
      // sola a esta persona» — antes esto no hacía nada, y el dueño lo vivió
      // como que no se podía "limpiar" un mes de varios pagadores hasta
      // dejar uno solo (3-sep-2026).
      const otros = block.payers.filter((p) => p.party_id !== partyId).map((p) => p.name);
      if (otros.length > 0) {
        setPending({
          kind: 'simple',
          label: `Dejar solo a ${partyName} en ${rangeLabel}: ${otros.join(', ')} ya no paga${otros.length > 1 ? 'n' : ''}.`,
          action: { type: 'set', months: block.months, members: [{ party_id: partyId, percentage: 100 }] },
        });
      }
      return;
    }

    const equalIds = Array.from(new Set([...block.payers.map((p) => p.party_id), partyId]));
    const pct = partesIguales(equalIds.length);
    setPending({
      kind: 'choice',
      label: `${partyName} sobre ${rangeLabel}, que ya paga ${block.payers.map((p) => p.name).join(', ')}.`,
      equal: { type: 'set', months: block.months, members: equalIds.map((id, i) => ({ party_id: id, percentage: pct[i]! })) },
      replace: { type: 'set', months: block.months, members: [{ party_id: partyId, percentage: 100 }] },
      replaceLabel: `Reemplazar por ${partyName}`,
    });
  }

  /* Mover el cuerpo de un bloque de override (nunca uno de la regla: no hay
     excepción que "quitar" de un mes que nunca tuvo una). */
  function onBlockBodyDown(block: DistBlock, e: React.PointerEvent<HTMLDivElement>) {
    if (block.locked || block.source !== 'override') return;
    e.currentTarget.setPointerCapture(e.pointerId);
    setBlockDrag({ kind: 'move', block, overPeriod: null });
  }
  /* Extender el borde derecho: sirve tanto para una excepción como para la
     regla — extender la regla crea excepciones nuevas con la misma gente. */
  function onBlockHandleDown(block: DistBlock, e: React.PointerEvent<HTMLSpanElement>) {
    if (block.locked || block.source === 'empty' || block.source === 'skip') return;
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    setBlockDrag({ kind: 'extend', block, overPeriod: null });
  }
  function onBlockDragMove() {
    return (e: React.PointerEvent) => {
      if (!blockDrag) return;
      const period = periodAtX(headRef.current, e.clientX);
      setBlockDrag((d) => (d ? { ...d, overPeriod: period } : d));
    };
  }
  function onBlockDragUp() {
    return () => {
      if (blockDrag) commitBlockDrag(blockDrag);
      setBlockDrag(null);
    };
  }

  function commitBlockDrag(drag: { kind: 'move' | 'extend'; block: DistBlock; overPeriod: string | null }) {
    const { kind, block, overPeriod } = drag;
    if (!overPeriod) return;
    const months = grid.months;
    const startIdx = months.indexOf(overPeriod);
    if (startIdx < 0) return;

    if (kind === 'move') {
      const span = block.months.length;
      if (overPeriod === block.months[0]) return;
      if (startIdx + span > months.length) return; // no se corre del año cargado — TODO: soportar cruzar de año.
      const destMonths = months.slice(startIdx, startIdx + span);
      if (destMonths.some((p) => blockByPeriod.get(p)?.locked)) return; // nunca se pisa un mes ya cobrado.
      setPending({
        kind: 'simple',
        label: `Mover ${monthRangeLabel(block.months)} a ${monthRangeLabel(destMonths)}.`,
        action: { type: 'move', originMonths: block.months, destMonths, members: payersToBody(block.payers) },
      });
      return;
    }

    // Extender: solo hacia meses posteriores al último del bloque.
    const lastIdx = months.indexOf(block.months[block.months.length - 1]!);
    if (startIdx <= lastIdx) return;
    const newMonths = months.slice(lastIdx + 1, startIdx + 1);
    if (newMonths.length === 0) return;
    if (newMonths.some((p) => blockByPeriod.get(p)?.locked)) return;
    setPending({
      kind: 'simple',
      label: `Extender ${monthRangeLabel(block.months)} hasta ${periodLabelShort(overPeriod)}.`,
      action: { type: 'set', months: newMonths, members: payersToBody(block.payers) },
    });
  }

  return (
    <div>
      <div className="row row--wrap mb-2" style={{ justifyContent: 'space-between' }}>
        <p className="small muted" style={{ maxWidth: 560, margin: 0 }}>
          Arrastrá una persona sobre un bloque, o movelo/extendelo tirando de su borde derecho. Nada de
          esto cambia el reparto de los demás meses — es una excepción, no una versión nueva de la
          distribución.
        </p>
        <select className="select" value={year} onChange={(e) => setYear(Number(e.target.value))} style={{ width: 110 }} aria-label="Año">
          {[year + 1, year, year - 1].map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      <div className="dist-legend mb-2">
        <span className="is-rule">Regla automática</span>
        <span className="is-override">Override manual</span>
      </div>

      {candidates.length > 0 && (
        <div className="payer-legend mb-2">
          {candidates.map((c) => (
            <button
              key={c.party_id} type="button" className="payer-chip"
              style={{ '--payer-color': payerColor(c.party_id) } as React.CSSProperties}
              onPointerDown={(e) => onChipDown(c, e)}
              onPointerMove={onChipMove}
              onPointerUp={onChipUp}
              onPointerCancel={() => setChipDrag(null)}
            >
              <span className="payer-chip__dot" />
              {c.name}
            </button>
          ))}
        </div>
      )}

      {/* Cabecera y bloques comparten el mismo grid de 12 columnas y el mismo
          scroll horizontal (`.dist-timeline__scroll`), así que un mes de la
          cabecera siempre cae exactamente sobre su bloque, en cualquier
          ancho — antes la cabecera se achicaba a 6 columnas bajo 900px sin
          que los bloques la siguieran. */}
      <div className="dist-timeline__scroll">
        <div className="dist-timeline" ref={headRef}>
          {grid.months.map((m) => (
            <div key={m} className={`dist-timeline__head${m === currentMonth ? ' is-current' : ''}`} data-period={m}>
              {periodLabelShort(m)}
            </div>
          ))}
        </div>

        <div className="dist-timeline mt-1">
          {blocks.map((block) => {
          const soloColor = block.payers.length === 1 ? payerColor(block.payers[0]!.party_id) : undefined;
          const dropTarget = chipDrag?.overKey === block.key || blockDrag?.block.key === block.key;
          const cls = [
            'dist-block',
            block.source === 'override' ? 'dist-block--override' : '',
            block.payers.length > 1 ? 'dist-block--split' : '',
            block.source === 'skip' ? 'dist-block--skip' : '',
            block.source === 'empty' ? 'dist-block--empty' : '',
            dropTarget ? 'is-selected' : '',
          ].filter(Boolean).join(' ');

          return (
            <div
              key={block.key}
              className={cls}
              style={{
                gridColumn: `span ${block.months.length}`,
                ...(soloColor ? { '--payer-color': soloColor } : {}),
              } as React.CSSProperties}
              data-block-start={block.key}
              data-locked={block.locked ? '1' : undefined}
              role="button"
              tabIndex={0}
              title={block.locked ? block.lockReason ?? 'Mes ya cobrado' : undefined}
              onClick={() => !block.locked && setEditingBlock(block)}
              onKeyDown={(e) => { if (!block.locked && (e.key === 'Enter' || e.key === ' ')) setEditingBlock(block); }}
              onPointerDown={(e) => onBlockBodyDown(block, e)}
              onPointerMove={onBlockDragMove()}
              onPointerUp={onBlockDragUp()}
              onPointerCancel={() => setBlockDrag(null)}
            >
              <span className="dist-block__tag">{monthRangeLabel(block.months)}</span>
              {block.source === 'skip' ? (
                <span>No se cobra</span>
              ) : block.source === 'empty' ? (
                <span>{block.locked ? 'Cobrado' : 'Sin definir'}</span>
              ) : (
                <>
                  {block.payers.slice(0, 2).map((p) => (
                    <span key={p.party_id} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                      <span className="payer-chip__dot" style={{ '--payer-color': payerColor(p.party_id) } as React.CSSProperties} />
                      {p.name}
                    </span>
                  ))}
                  {block.payers.length > 2 && <span className="muted small">y {block.payers.length - 2} más</span>}
                  {block.monthlyTotal && <Money value={block.monthlyTotal} />}
                </>
              )}
              {block.locked && <Badge tone="neutral" plain>cobrado</Badge>}
              {!block.locked && block.source !== 'empty' && (
                <span
                  className="dist-block__handle"
                  onPointerDown={(e) => onBlockHandleDown(block, e)}
                  onClick={(e) => e.stopPropagation()}
                />
              )}
            </div>
          );
          })}
        </div>
      </div>

      {chipDrag && (
        <div className="payer-drag-ghost" style={{ left: chipDrag.x, top: chipDrag.y, '--payer-color': payerColor(chipDrag.partyId) } as React.CSSProperties}>
          {chipDrag.name}
        </div>
      )}

      {write.error && <Note tone="danger">{write.error.message}</Note>}

      {pending?.kind === 'simple' && (
        <div className="row row--wrap mt-2 dist-confirm">
          <span className="small">{pending.label}</span>
          <span className="spacer" />
          <Button onClick={() => setPending(null)}>Cancelar</Button>
          <Button variant="primary" disabled={write.isPending} onClick={() => escribir(pending.action, { onSuccess: () => setPending(null) })}>
            {write.isPending ? 'Guardando…' : 'Confirmar'}
          </Button>
        </div>
      )}
      {pending?.kind === 'choice' && (
        <div className="row row--wrap mt-2 dist-confirm">
          <span className="small">{pending.label}</span>
          <span className="spacer" />
          <Button onClick={() => setPending(null)}>Cancelar</Button>
          <Button disabled={write.isPending} onClick={() => escribir(pending.equal, { onSuccess: () => setPending(null) })}>
            Dividir por igual
          </Button>
          <Button variant="primary" disabled={write.isPending} onClick={() => escribir(pending.replace, { onSuccess: () => setPending(null) })}>
            {pending.replaceLabel}
          </Button>
        </div>
      )}

      {promocion && (
        <div className="mt-2">
          <Note tone="warn">
            <div className="row row--wrap" style={{ gap: 8, alignItems: 'center' }}>
              <span className="small">
                {promocion.partyIds.map(nombreDe).join(' / ')} así hacia adelante, desde{' '}
                {periodLabelShort(promocion.anchor)}?
              </span>
              <span className="spacer" />
              <Button onClick={() => setSemilla(null)}>Ahora no</Button>
              <Button
                variant="primary" disabled={promover.isPending}
                onClick={() => promover.mutate({ anchor: promocion.anchor, partyIds: promocion.partyIds })}
              >
                {promover.isPending ? 'Guardando…' : 'Sí, que rote'}
              </Button>
            </div>
            {promover.error && <p className="small" style={{ color: 'var(--danger)', margin: '6px 0 0' }}>{promover.error.message}</p>}
          </Note>
        </div>
      )}

      {editingBlock && (
        <BlockModal
          block={editingBlock}
          candidates={candidates}
          write={write}
          onWritten={(months) => setSemilla(months[months.length - 1] ?? null)}
          onClose={() => setEditingBlock(null)}
        />
      )}
    </div>
  );
}

/** Editar un bloque entero (uno o varios meses seguidos) de una sola vez. */
function BlockModal({ block, candidates, write, onWritten, onClose }: {
  block: DistBlock; candidates: Candidate[]; write: ReturnType<typeof useWritePeriods>;
  /** Igual que `escribir` en `Timeline`: avisa qué meses quedaron tocados,
   *  para que la detección de rotación tenga desde dónde mirar. */
  onWritten: (months: string[]) => void;
  onClose: () => void;
}) {
  const [mode, setMode] = useState<'CUSTOM' | 'SKIP' | 'DEFAULT'>(
    block.source === 'override' ? 'CUSTOM' : block.source === 'skip' ? 'SKIP' : 'DEFAULT');
  const [rows, setRows] = useState<{ party_id: string; percentage: number }[]>(() =>
    block.payers.length > 0
      ? block.payers.map((p, i) => ({ party_id: p.party_id, percentage: p.percentage ?? partesIguales(block.payers.length)[i]! }))
      : candidates.slice(0, 1).map((c) => ({ party_id: c.party_id, percentage: 100 })));

  const sum = rows.reduce((a, r) => a + r.percentage, 0);
  const percentOk = Math.abs(sum - 100) < 0.0001;
  const valid = mode === 'DEFAULT' || mode === 'SKIP' || (rows.length > 0 && percentOk);

  function submit() {
    const action: WriteAction = mode === 'DEFAULT'
      ? { type: 'clear', months: block.months }
      : mode === 'SKIP'
        ? { type: 'skip', months: block.months }
        : { type: 'set', months: block.months, members: rows.map((r) => ({ party_id: r.party_id, percentage: r.percentage })) };
    write.mutate(action, { onSuccess: () => { onWritten(block.months); onClose(); } });
  }

  return (
    <Modal
      title={monthRangeLabel(block.months)}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="primary" disabled={!valid || write.isPending} onClick={submit}>
            {write.isPending ? 'Guardando…' : 'Guardar'}
          </Button>
        </>
      }
    >
      {write.error && <Note tone="danger">{write.error.message}</Note>}

      <Note tone="info">
        Vale <strong>solo para {monthRangeLabel(block.months)}</strong>. El reparto de los demás meses
        no cambia.
      </Note>

      <div className="radios mt-2">
        <label className={`radiocard${mode === 'DEFAULT' ? ' is-on' : ''}`}>
          <input type="radio" checked={mode === 'DEFAULT'} onChange={() => setMode('DEFAULT')} />
          <span>
            <span className="radiocard__t">Como siempre</span>
            <span className="radiocard__d">Se reparte según la distribución del paquete.</span>
          </span>
        </label>
        <label className={`radiocard${mode === 'CUSTOM' ? ' is-on' : ''}`}>
          <input type="radio" checked={mode === 'CUSTOM'} onChange={() => setMode('CUSTOM')} />
          <span>
            <span className="radiocard__t">Estos meses los pagan otros</span>
            <span className="radiocard__d">Elegís quién y en qué proporción, solo para este bloque.</span>
          </span>
        </label>
        <label className={`radiocard${mode === 'SKIP' ? ' is-on' : ''}`}>
          <input type="radio" checked={mode === 'SKIP'} onChange={() => setMode('SKIP')} />
          <span>
            <span className="radiocard__t">No cobrar {block.months.length > 1 ? 'estos meses' : 'este mes'}</span>
            <span className="radiocard__d">No se genera cobro. Queda el motivo registrado.</span>
          </span>
        </label>
      </div>

      {mode === 'CUSTOM' && (
        <div className="mt-3">
          {rows.map((row, i) => (
            <div className="row mb-1" key={row.party_id}>
              <select
                className="select" value={row.party_id}
                onChange={(e) => setRows((prev) => prev.map((r, j) => (j === i ? { ...r, party_id: e.target.value } : r)))}
              >
                {candidates.map((c) => <option key={c.party_id} value={c.party_id}>{c.name}</option>)}
              </select>
              <input
                className="input input--sm input--money" type="number" step="0.01" min="0" max="100"
                value={row.percentage} style={{ width: 90 }}
                onChange={(e) => setRows((prev) => prev.map((r, j) => (j === i ? { ...r, percentage: Number(e.target.value) } : r)))}
              />
              <span className="muted small">%</span>
              <Button onClick={() => setRows((prev) => prev.filter((_, j) => j !== i))}>Quitar</Button>
            </div>
          ))}
          <div className="row mt-2">
            <Button
              onClick={() => {
                const free = candidates.find((c) => !rows.some((r) => r.party_id === c.party_id));
                if (free) setRows((prev) => [...prev, { party_id: free.party_id, percentage: 0 }]);
              }}
              disabled={rows.length >= candidates.length}
            >
              Agregar pagador
            </Button>
            <span className="spacer" />
            <span className={`small${percentOk ? ' muted' : ''}`} style={percentOk ? undefined : { color: 'var(--danger)' }}>
              Suman {sum.toFixed(2)}% · deben sumar 100%
            </span>
          </div>
        </div>
      )}
    </Modal>
  );
}


/* ── La regla vigente: modo, personas, rotación o montos ──────────────────── */

interface DraftMember { party_id: string; name: string; percentage?: number; fixed_amount?: number }
/**
 * `splitKind` no se guarda en el servidor — es una comodidad de esta
 * pantalla. Bajo SPLIT, el criterio real lo decide qué campo trae poblado
 * cada integrante (ver `packages/shared/src/enums.ts`); acá se guarda aparte
 * solo para saber qué inputs mostrar mientras se edita.
 */
type SplitKind = 'EQUAL' | 'PERCENT' | 'AMOUNT';
interface Draft { mode: AllocationMode; splitKind: SplitKind; from: string; rows: DraftMember[] }

function draftFromCurrent(current: Allocation | undefined, members: Member[]): Draft {
  const splitKind: SplitKind = members.some((m) => m.percentage != null)
    ? 'PERCENT'
    : members.some((m) => m.fixed_amount_minor != null)
      ? 'AMOUNT'
      : 'EQUAL';
  return {
    mode: current?.mode ?? 'SPLIT',
    splitKind,
    from: startOfMonth(today()),
    rows: members.map((m) => ({
      party_id: m.party_id, name: m.display_name,
      percentage: m.percentage ?? undefined, fixed_amount: m.fixed_amount_minor ?? undefined,
    })),
  };
}

/** Lista de la rotación mensual: se reordena arrastrando, con Pointer Events. */
function RotationList({ rows, onReorder, onRemove }: {
  rows: DraftMember[]; onReorder: (next: DraftMember[]) => void; onRemove: (partyId: string) => void;
}) {
  const [dragId, setDragId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);

  function onDown(id: string, e: React.PointerEvent<HTMLDivElement>) {
    e.currentTarget.setPointerCapture(e.pointerId);
    setDragId(id);
  }
  function onMove(e: React.PointerEvent<HTMLDivElement>) {
    if (!dragId) return;
    const el = document.elementFromPoint(e.clientX, e.clientY);
    const row = el?.closest<HTMLElement>('[data-row-id]');
    setOverId(row?.dataset.rowId ?? null);
  }
  function onUp() {
    if (dragId && overId && dragId !== overId) {
      const from = rows.findIndex((r) => r.party_id === dragId);
      const to = rows.findIndex((r) => r.party_id === overId);
      if (from >= 0 && to >= 0) {
        const next = [...rows];
        const [moved] = next.splice(from, 1);
        next.splice(to, 0, moved!);
        haptic.success(); // HAPTIC_FEEDBACK
        onReorder(next);
      }
    }
    setDragId(null);
    setOverId(null);
  }

  /** Subir/bajar un turno: el mismo `onReorder` que ya usa el arrastre, para
   *  quien no quiere (o no puede, con el dedo) arrastrar con precisión. */
  function moveBy(i: number, delta: -1 | 1) {
    const j = i + delta;
    if (j < 0 || j >= rows.length) return;
    const next = [...rows];
    const tmp = next[i]!;
    next[i] = next[j]!;
    next[j] = tmp;
    haptic.success(); // HAPTIC_FEEDBACK
    onReorder(next);
  }

  return (
    <div className="rotation-list mt-2">
      {rows.map((r, i) => (
        <div
          key={r.party_id}
          data-row-id={r.party_id}
          className={`rotation-row${dragId === r.party_id ? ' is-dragging' : ''}${overId === r.party_id && dragId !== r.party_id ? ' is-over' : ''}`}
          onPointerDown={(e) => onDown(r.party_id, e)}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerCancel={() => { setDragId(null); setOverId(null); }}
        >
          <span className="rotation-row__handle" aria-hidden="true"><Icon name="arrastrar" size={14} /></span>
          <span className="rotation-row__turn">Turno {i + 1}</span>
          <span className="strong spacer">{r.name}</span>
          {/* Subir/bajar: alternativa al arrastre para quien no puede (o no
              quiere) tirar con precisión. La familia de íconos no tiene un
              par vertical —los chevrones que ya existen apuntan a los lados,
              para el mes anterior/siguiente— así que va con texto, como
              «Quitar» acá al lado. */}
          {/* `stopPropagation` en el `pointerDown` de los tres: la fila entera
              tiene su propio `onPointerDown` para el arrastre (`onDown`, más
              arriba), y sin cortarla acá el `pointerdown` del botón burbujea
              hasta la fila, que captura el puntero — con el puntero
              capturado por la fila, el `click` nunca le llega al botón. Es el
              mismo problema, y la misma solución, que ya tiene el "handle" de
              extender un bloque en `Timeline` (`onBlockHandleDown`). */}
          <span className="rotation-row__reorder">
            <Button
              aria-label={`Subir a ${r.name} al turno ${i}`} disabled={i === 0}
              onPointerDown={(e) => e.stopPropagation()} onClick={() => moveBy(i, -1)}
            >
              ↑
            </Button>
            <Button
              aria-label={`Bajar a ${r.name} al turno ${i + 2}`} disabled={i === rows.length - 1}
              onPointerDown={(e) => e.stopPropagation()} onClick={() => moveBy(i, 1)}
            >
              ↓
            </Button>
          </span>
          <Button onPointerDown={(e) => e.stopPropagation()} onClick={() => onRemove(r.party_id)}>Quitar</Button>
        </div>
      ))}
      {rows.length === 0 && <p className="muted small">Agregá al menos un pagador para armar el turno.</p>}
    </div>
  );
}

function RulePanel({ subscriptionId, current, currentMembers, candidates, totalMinor, onClose }: {
  subscriptionId: string; current: Allocation | undefined; currentMembers: Member[];
  candidates: Candidate[]; totalMinor: number; onClose: () => void;
}) {
  const [draft, setDraft] = useState<Draft>(() => draftFromCurrent(current, currentMembers));
  const undoStack = useRef<Draft[]>([]);
  const [canUndo, setCanUndo] = useState(false);
  const editSnapshot = useRef<Draft | null>(null);

  /** Cada acción discreta (no cada tecla) empuja el estado anterior a la pila. */
  function change(mutator: (d: Draft) => Draft) {
    setDraft((prev) => {
      undoStack.current.push(prev);
      if (undoStack.current.length > 25) undoStack.current.shift();
      setCanUndo(true);
      return mutator(prev);
    });
  }
  function undo() {
    const prev = undoStack.current.pop();
    if (prev) setDraft(prev);
    setCanUndo(undoStack.current.length > 0);
  }
  function beginEdit() { editSnapshot.current = draft; }
  function commitEdit() {
    if (editSnapshot.current && JSON.stringify(editSnapshot.current) !== JSON.stringify(draft)) {
      undoStack.current.push(editSnapshot.current);
      if (undoStack.current.length > 25) undoStack.current.shift();
      setCanUndo(true);
    }
    editSnapshot.current = null;
  }

  const usesPercent = draft.mode === 'SPLIT' && draft.splitKind === 'PERCENT';
  const usesAmount = draft.mode === 'SPLIT' && draft.splitKind === 'AMOUNT';
  const percentSum = draft.rows.reduce((a, r) => a + (r.percentage ?? 0), 0);
  const percentOk = !usesPercent || Math.abs(percentSum - 100) < 0.0001;

  const unitCandidate = candidates.find((candidate) => candidate.kind === 'UNIT');
  const unitIds = new Set(candidates.filter((candidate) => candidate.kind === 'UNIT')
    .map((candidate) => candidate.party_id));
  const hasUnit = draft.rows.some((row) => unitIds.has(row.party_id));
  const hasPeople = draft.rows.some((row) => !unitIds.has(row.party_id));
  const payerMode: 'UNIT' | 'PEOPLE' = hasUnit ? 'UNIT' : 'PEOPLE';
  const mixedMode = hasUnit && hasPeople;

  // La modalidad es excluyente también en el selector: la entidad del
  // consultorio nunca aparece junto con las personas como si fueran sumables.
  const available = candidates.filter((candidate) =>
    (payerMode === 'UNIT' ? candidate.kind === 'UNIT' : candidate.kind === 'MEMBER')
    && !draft.rows.some((row) => row.party_id === candidate.party_id));

  function setPayerMode(mode: 'UNIT' | 'PEOPLE') {
    if (mode === 'UNIT') {
      if (!unitCandidate) return;
      change((draft_) => ({
        ...draft_, mode: 'SPLIT', splitKind: 'EQUAL',
        rows: [{ party_id: unitCandidate.party_id, name: unitCandidate.name }],
      }));
      return;
    }
    change((draft_) => ({
      ...draft_, rows: draft_.rows.filter((row) => !unitIds.has(row.party_id)),
    }));
  }

  function addMember(c: Candidate) {
    change((d) => ({ ...d, rows: [...d.rows, { party_id: c.party_id, name: c.name }] }));
  }
  function removeMember(partyId: string) {
    change((d) => ({ ...d, rows: d.rows.filter((r) => r.party_id !== partyId) }));
  }
  function equalSplit() {
    change((d) => ({
      ...d, mode: 'SPLIT', splitKind: 'EQUAL',
      rows: d.rows.map((r) => ({ ...r, percentage: undefined, fixed_amount: undefined })),
    }));
  }
  function customPercentages() {
    change((d) => ({
      ...d, mode: 'SPLIT', splitKind: 'PERCENT',
      rows: d.rows.map((r, i) => ({ ...r, fixed_amount: undefined, percentage: r.percentage ?? partesIguales(d.rows.length)[i]! })),
    }));
  }
  // "Personalizar montos" reparte por céntimos, no por porcentaje — de ahí
  // que autocomplete el segundo pagador como `monto − valor` cuando son dos.
  function customAmounts() {
    change((d) => ({
      ...d, mode: 'SPLIT', splitKind: 'AMOUNT',
      rows: d.rows.map((r) => ({ ...r, percentage: undefined, fixed_amount: r.fixed_amount ?? Math.round(totalMinor / d.rows.length) })),
    }));
  }
  function toRotation() {
    change((d) => ({
      ...d, mode: 'ROTATION', splitKind: 'EQUAL',
      rows: d.rows.map((r) => ({ ...r, percentage: undefined, fixed_amount: undefined })),
    }));
  }
  function setAmount(partyId: string, value: number) {
    setDraft((d) => {
      const rows = d.rows.map((r) => (r.party_id === partyId ? { ...r, fixed_amount: value } : r));
      if (rows.length === 2) {
        const mine = rows.find((r) => r.party_id === partyId);
        const other = rows.find((r) => r.party_id !== partyId);
        if (mine && other) other.fixed_amount = Math.max(0, totalMinor - (mine.fixed_amount ?? 0));
      }
      return { ...d, rows };
    });
  }
  function setPercentage(partyId: string, value: number) {
    setDraft((d) => ({ ...d, rows: d.rows.map((r) => (r.party_id === partyId ? { ...r, percentage: value } : r)) }));
  }

  const save = useApiMutation(
    () => api.post('/admin/allocations', {
      subscription_id: subscriptionId,
      effective_from: draft.from,
      mode: draft.mode,
      rotation_anchor: draft.mode === 'ROTATION' ? draft.from : undefined,
      members: draft.rows.map((r, i) => ({
        party_id: r.party_id,
        ordinal: i + 1,
        percentage: usesPercent ? r.percentage : undefined,
        fixed_amount: usesAmount ? r.fixed_amount : undefined,
        rotation_seq: draft.mode === 'ROTATION' ? i : undefined,
      })),
    }),
    ['/admin/subscriptions', '/admin/preview', '/admin/dashboard'],
    { onSuccess: onClose },
  );

  const valid = draft.rows.length > 0 && !mixedMode && percentOk
    && (payerMode !== 'UNIT' || (draft.mode === 'SPLIT' && draft.rows.length === 1))
    && (!usesAmount || draft.rows.every((r) => r.fixed_amount != null));

  return (
    <Card title="Editar reparto" actions={<Button onClick={onClose}>Cerrar</Button>}>
      {save.error && <Note tone="danger">{save.error.message}</Note>}

      <Note tone="info">
        Esto no cambia los meses ya cobrados. Se cierra el reparto vigente y se abre uno nuevo desde la
        fecha que elijas.
      </Note>

      <div className="payer-mode mt-2">
        <div className="row row--wrap">
          <Button variant={payerMode === 'UNIT' ? 'primary' : undefined}
            disabled={!unitCandidate} onClick={() => setPayerMode('UNIT')}>
            Paga el consultorio
          </Button>
          <Button variant={payerMode === 'PEOPLE' ? 'primary' : undefined}
            onClick={() => setPayerMode('PEOPLE')}>
            Pagan personas
          </Button>
        </div>
        {payerMode === 'UNIT' ? (
          <p className="small muted">
            La factura completa se emitirá a <strong>{unitCandidate?.legal_name ?? unitCandidate?.name ?? 'la entidad fiscal del consultorio'}</strong>
            {unitCandidate?.tax_id ? ` · cédula ${unitCandidate.tax_id}` : ' · falta completar su cédula fiscal'}.
          </p>
        ) : (
          <p className="small muted">
            Cada parte se cobrará y facturará a las personas seleccionadas según esta regla. La identidad fiscal del consultorio queda fuera.
          </p>
        )}
        {!unitCandidate && (
          <Note tone="warn">Este consultorio todavía no tiene una identidad fiscal propia configurada.</Note>
        )}
        {mixedMode && (
          <Note tone="danger">La configuración anterior mezcla consultorio y personas. Elegí una de las dos modalidades para continuar.</Note>
        )}
      </div>

      <div className="row row--wrap mt-2" style={{ justifyContent: 'space-between' }}>
        <Field label="Rige desde" required hint="El primer período que va a usar este reparto.">
          <input className="input" type="date" value={draft.from} onChange={(e) => change((d) => ({ ...d, from: e.target.value }))} />
        </Field>
        <Field label="Modo de reparto" required hint={ALLOCATION_MODE_HINT[draft.mode]}>
          {payerMode === 'UNIT' ? (
            <Badge tone="info">100% todos los meses</Badge>
          ) : (
            <div className="row" style={{ gap: 6 }}>
              {ALLOCATION_MODE.map((m) => (
                <Button
                  key={m}
                  variant={draft.mode === m ? 'primary' : undefined}
                  onClick={() => (m === 'ROTATION' ? toRotation() : equalSplit())}
                >
                  {ALLOCATION_MODE_LABEL[m]}
                </Button>
              ))}
            </div>
          )}
        </Field>
        <Button disabled={!canUndo} onClick={undo}>Deshacer</Button>
      </div>

      {draft.mode === 'SPLIT' && payerMode === 'PEOPLE' && (
        <div className="row mt-2">
          <Button variant={draft.splitKind === 'EQUAL' ? 'primary' : undefined} onClick={equalSplit}>
            Dividir por igual
          </Button>
          <Button variant={draft.splitKind === 'PERCENT' ? 'primary' : undefined} onClick={customPercentages}>
            Personalizar porcentajes
          </Button>
          <Button variant={draft.splitKind === 'AMOUNT' ? 'primary' : undefined} onClick={customAmounts}>
            Personalizar montos
          </Button>
        </div>
      )}

      {draft.mode === 'ROTATION' ? (
        // La cadencia ("cada 2, 3, 4, 6 meses") no existe en el dominio: solo
        // hay rotación mensual (`subscription_allocations` no tiene un campo
        // de cadencia). No se agrega ese control acá — decisión ya tomada.
        <RotationList
          rows={draft.rows}
          onReorder={(next) => change((d) => ({ ...d, rows: next }))}
          onRemove={removeMember}
        />
      ) : (
        <div className="mt-2" style={{ display: 'grid', gap: 6 }}>
          {draft.rows.map((r) => (
            // `row--wrap` como en el resto del archivo: el nombre más los dos
            // campos más «Quitar» piden ~500px, y un teléfono da ~300.
            <div className="row row--wrap" key={r.party_id}>
              <span className="strong" style={{ flex: '1 1 160px', minWidth: 0 }}>{r.name}</span>
              {usesPercent && (
                <>
                  <input
                    className="input input--sm input--money" type="number" step="0.01" min="0" max="100"
                    value={r.percentage ?? ''} style={{ width: 90 }}
                    onFocus={beginEdit} onBlur={commitEdit}
                    onChange={(e) => setPercentage(r.party_id, Number(e.target.value))}
                  />
                  <span className="muted small">%</span>
                </>
              )}
              {usesAmount && (
                <input
                  className="input input--sm input--money" type="number" step="100" min="0"
                  value={r.fixed_amount ?? ''} style={{ width: 130 }}
                  onFocus={beginEdit} onBlur={commitEdit}
                  onChange={(e) => setAmount(r.party_id, Number(e.target.value))}
                />
              )}
              <span className="spacer" />
              <Button onClick={() => removeMember(r.party_id)}>Quitar</Button>
            </div>
          ))}
          {draft.rows.length === 0 && <p className="muted small">Agregá al menos un pagador.</p>}
        </div>
      )}

      {usesPercent && (
        <p className={`small mt-2${percentOk ? ' muted' : ''}`} style={percentOk ? undefined : { color: 'var(--danger)' }}>
          Suman {percentSum.toFixed(2)}% · deben sumar 100%
        </p>
      )}

      {available.length > 0 && (
        <div className="payer-legend mt-2">
          {available.map((c) => (
            <button
              key={c.party_id} type="button" className="payer-chip" style={{ cursor: 'pointer' }}
              onClick={() => addMember(c)}
            >
              <span className="payer-chip__dot" style={{ '--payer-color': payerColor(c.party_id) } as React.CSSProperties} />
              + {c.name}
            </button>
          ))}
        </div>
      )}

      <div className="row mt-3">
        <span className="spacer" />
        <Button variant="primary" disabled={!valid || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'Guardando…' : 'Guardar reparto'}
        </Button>
      </div>
    </Card>
  );
}

/* ── El editor completo ────────────────────────────────────────────────────── */

export function DistributionEditor({ subscriptionId }: { subscriptionId: string }) {
  const detail = useApi<DistributionDetail>(`/admin/subscriptions/${subscriptionId}`);
  const currentYear = Number(today().slice(0, 4));
  const [year, setYear] = useState(currentYear);
  const periods = useApi<GridData>(`/admin/subscriptions/${subscriptionId}/periods?year=${year}`);
  const unitId = detail.data?.subscription.service_unit_id ?? null;
  const doctors = useApi<{ doctors: Candidate[] }>(unitId ? `/admin/units/${unitId}/doctors` : null);
  const [editingRule, setEditingRule] = useState(false);
  /* Arranca colapsada (pedido del dueño, 3-sep-2026: "la mejor interfaz es
     la del año mes a mes" — la regla es el respaldo, no la pantalla
     principal). Colapsarla no tiene sentido cuando no hay nada que mostrar
     todavía: ahí el "Sin reparto definido" es justo lo que hay que ver. */
  const [ruleExpanded, setRuleExpanded] = useState(false);

  const confirm = useApiMutation(
    (allocationId: string) => api.post(`/admin/allocations/${allocationId}/confirm`),
    ['/admin/subscriptions', '/admin/dashboard'],
  );

  return (
    <Async query={detail}>
      {({ subscription: s, allocations, members }) => {
        const current = allocations.find((a) => !a.effective_to) ?? allocations[0];
        const currentMembers = members.filter((m) => m.allocation_id === current?.id);
        const candidates = doctors.data?.doctors ?? currentMembers.map((m) => ({
          party_id: m.party_id, name: m.display_name, kind: m.kind,
          legal_name: m.legal_name, tax_id: m.tax_id, billing_email: null,
        }));
        const totalMinor = s.price_override_minor ?? s.base_price_minor;

        // Colapsada, sin nada abierto todavía: solo un resumen de una línea.
        // No hay nada que "Confirmar" ni "Editar" desde acá — para eso hay
        // que expandir primero.
        const mostrar = ruleExpanded || !current;

        return (
          <div style={{ display: 'grid', gap: 14 }}>
            <Card
              title="Regla por defecto"
              flush
              actions={current && (
                <Button onClick={() => setRuleExpanded((v) => !v)}>
                  {ruleExpanded ? 'Colapsar' : 'Expandir'}
                </Button>
              )}
            >
              {!mostrar ? (
                <p className="small muted" style={{ margin: 0, padding: '12px 16px 16px' }}>
                  A qué vuelve un mes si nadie lo tocó puntualmente en «El año mes a mes».
                  Hoy: <strong>{ALLOCATION_MODE_LABEL[current!.mode]}</strong>
                  {!current!.confirmed && ' (sin confirmar)'}.
                </p>
              ) : !current ? (
                <Empty
                  icon="reparto-regla" title="Sin reparto definido" hint="Un paquete sin reparto nunca genera cobros."
                  action={<Button variant="primary" onClick={() => setEditingRule(true)}>Definir</Button>}
                />
              ) : (
                <div style={{ padding: '12px 16px 16px' }}>
                  <Badge tone={current.confirmed ? 'ok' : 'warn'}>{ALLOCATION_MODE_LABEL[current.mode]}</Badge>
                  <p className="small muted mt-1">
                    {ALLOCATION_MODE_HINT[current.mode]}
                    {current.rotation_anchor && ` Empezó en ${dateLabel(current.rotation_anchor)}.`}
                  </p>
                  {!current.confirmed && (
                    <Note tone="warn">
                      Este reparto no fue validado contra la facturación real. Confirmalo cuando sepas
                      cómo se divide de verdad.
                    </Note>
                  )}
                  <div style={{ display: 'grid', gap: 4, marginTop: 10 }}>
                    {currentMembers.map((m) => (
                      <div className="row" key={m.id}>
                        <span className="payer-chip__dot" style={{ '--payer-color': payerColor(m.party_id) } as React.CSSProperties} />
                        <span className="strong">{m.display_name}</span>
                        {current.mode === 'ROTATION' && <span className="muted small">turno {(m.rotation_seq ?? 0) + 1}</span>}
                        <span className="spacer" />
                        <span className="small muted">
                          {m.percentage != null ? `${m.percentage}%`
                            : m.fixed_amount_minor != null ? <Money value={m.fixed_amount_minor} />
                              : current.mode === 'ROTATION' ? '100%' : `${(100 / currentMembers.length).toFixed(2)}%`}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="row mt-2">
                    <span className="spacer" />
                    {!current.confirmed && (
                      <Button disabled={confirm.isPending} onClick={() => confirm.mutate(current.id)}>
                        Confirmar reparto
                      </Button>
                    )}
                    <Button onClick={() => setEditingRule((v) => !v)}>
                      {editingRule ? 'Cerrar edición' : 'Editar reparto'}
                    </Button>
                  </div>
                </div>
              )}
            </Card>

            {mostrar && editingRule && (
              <RulePanel
                subscriptionId={subscriptionId}
                current={current}
                currentMembers={currentMembers}
                candidates={candidates}
                totalMinor={totalMinor}
                onClose={() => setEditingRule(false)}
              />
            )}

            <Card
              title="El año mes a mes"
              flush
            >
              <div style={{ padding: 16 }}>
                <Async query={periods}>
                  {(grid) => (
                    <Timeline
                      subscriptionId={subscriptionId} year={year} setYear={setYear} grid={grid}
                      candidates={candidates} current={current}
                    />
                  )}
                </Async>
              </div>
            </Card>

          </div>
        );
      }}
    </Async>
  );
}

/* ── El resumen compacto para el detalle de un consultorio ────────────────── */

/**
 * El resumen que cabe en la columna angosta del perfil de un consultorio:
 * solo lo que hace falta para saber quién paga hoy, de un vistazo. El editor
 * completo — con la línea de tiempo de doce meses, el drag & drop y el panel
 * de la regla — necesita mucho más ancho del que esa columna tiene, así que
 * vive en un modal grande, no expandido en el mismo espacio angosto.
 */
export function DistributionSummary({ subscriptionId }: { subscriptionId: string }) {
  const [editing, setEditing] = useState(false);
  const detail = useApi<DistributionDetail>(`/admin/subscriptions/${subscriptionId}`);
  const year = Number(today().slice(0, 4));
  const periods = useApi<GridData>(`/admin/subscriptions/${subscriptionId}/periods?year=${year}`);

  return (
    <Card
      title="Distribución de pagos"
      actions={<Button variant="primary" onClick={() => setEditing(true)}>Editar reparto</Button>}
    >
      <Async query={detail}>
        {({ subscription: s, allocations, members }) => {
          const current = allocations.find((a) => !a.effective_to) ?? allocations[0];
          const currentMembers = members.filter((m) => m.allocation_id === current?.id);
          const currentMonth = startOfMonth(today());
          const totalMinor = s.price_override_minor ?? s.base_price_minor;

          const grid = periods.data;
          let nextChangeLabel = 'Sin cambios este año';
          let nextMonths: ResolvedMonth[] = [];
          // Quién paga *este* mes en particular — no la nómina completa de la
          // regla: en rotación, `currentMembers` son todos los que rotan, no
          // solo el que le toca ahora. Sale de `/periods` (regla + excepción
          // ya resueltas); mientras esa consulta no cargó, se muestra un
          // aproximado con la nómina de la regla.
          let payingNow: DistPayer[] | null = null;
          if (grid) {
            // TODO: si el año cargado no alcanza (diciembre sin cambio a la
            // vista), habría que traer también enero del año siguiente; por
            // ahora la ventana de "próximos meses" no cruza el 31/12.
            const resolved = resolveMonths(grid);
            const idx = resolved.findIndex((r) => r.period === currentMonth);
            const from = idx >= 0 ? idx : 0;
            nextMonths = resolved.slice(from, from + 6);
            if (idx >= 0) {
              payingNow = resolved[idx]!.payers;
              /* Solo QUIÉN paga, no de dónde salió el mes: `payerKey` distingue
                 regla de excepción a propósito —para agrupar bloques en el
                 Timeline— pero acá eso hacía anunciar un "próximo cambio"
                 cuando el pagador seguía siendo el mismo y lo único que
                 cambiaba era que un mes venía de una excepción y el siguiente
                 de la regla. */
              const quienes = (r: ResolvedMonth): string =>
                r.payers.map((p) => p.party_id).sort().join('|');
              const here = quienes(resolved[idx]!);
              const change = resolved.slice(idx + 1).find((r) => quienes(r) !== here);
              if (change) nextChangeLabel = periodLabel(change.period);
            }
          }
          const payingNowLabel = payingNow
            ? (payingNow.length === 0 ? '—' : payingNow.length === 1 ? payingNow[0]!.name : `${payingNow.length} personas`)
            : (currentMembers.length === 0 ? '—'
              : currentMembers.length === 1 ? currentMembers[0]!.display_name
                : `${currentMembers.length} personas`);

          return (
            <>
              {/* `grid--2`, no `grid--4`: esta tarjeta vive en la columna angosta del
                  perfil del consultorio, y cuatro estadísticas en ese ancho es lo
                  que las cortaba a la mitad. */}
              <div className="grid grid--2">
                <Stat label="Paga actualmente" value={payingNowLabel} />
                <Stat label="Próximo cambio" value={nextChangeLabel} />
                <Stat label="Tipo de regla" value={current ? ALLOCATION_MODE_LABEL[current.mode] : 'Sin definir'} />
                <Stat label="Monto mensual" value={<Money value={totalMinor} size="lg" />} />
              </div>

              {nextMonths.length > 0 && (
                <div className="row row--wrap mt-2" style={{ gap: 8 }}>
                  {nextMonths.map((m) => (
                    <Badge key={m.period} tone="neutral" plain title={m.payers.map((p) => p.name).join(', ') || 'Sin definir'}>
                      {periodLabelShort(m.period)}: {m.payers.length === 0 ? '—' : m.payers.length === 1 ? m.payers[0]!.name : `${m.payers.length}p`}
                    </Badge>
                  ))}
                </div>
              )}

              {editing && (
                <Modal
                  title={`Distribución de pagos · ${s.unit_code}`}
                  xwide
                  onClose={() => setEditing(false)}
                >
                  <DistributionEditor subscriptionId={subscriptionId} />
                </Modal>
              )}
            </>
          );
        }}
      </Async>
    </Card>
  );
}
